import React, { useState, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Loader2, X, FileText, Image as ImageIcon, Video } from "lucide-react";

/**
 * FileUpload — Emergent Object Storage uploader.
 *
 * Props:
 *   kind: string — e.g. "d085", "aircraft_photo", "aircraft_video", "logo"
 *   accept: string — MIME accept string
 *   maxMB: number — max size in MB (client-side guard)
 *   multiple: boolean — allow multiple files
 *   value: string | string[] — file_id(s) already uploaded (renders thumb/pill)
 *   onChange(nextValue): parent gets the file_id(s)
 *   variant: "compact" | "gallery" — visual style
 *   testId: string
 */
export default function FileUpload({
  kind = "misc",
  accept = "image/*,application/pdf,video/mp4",
  maxMB = 200,
  multiple = false,
  value,
  onChange,
  variant = "compact",
  testId,
}) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const list = multiple ? (Array.isArray(value) ? value : (value ? [value] : [])) : (value ? [value] : []);

  const pick = () => inputRef.current?.click();

  const upload = async (files) => {
    setBusy(true);
    try {
      const uploaded = [];
      for (const file of files) {
        if (file.size > maxMB * 1024 * 1024) {
          toast.error(`${file.name}: exceeds ${maxMB}MB`);
          continue;
        }
        const fd = new FormData();
        fd.append("file", file);
        const r = await api.post(`/files/upload?kind=${encodeURIComponent(kind)}`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        uploaded.push(r.data.id);
      }
      if (uploaded.length) {
        if (multiple) onChange([...list, ...uploaded]);
        else onChange(uploaded[0]);
        toast.success(`Uploaded ${uploaded.length} file${uploaded.length === 1 ? "" : "s"}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = (idx) => {
    if (multiple) {
      const next = [...list];
      next.splice(idx, 1);
      onChange(next);
    } else {
      onChange("");
    }
  };

  const isPdf = accept.includes("pdf");
  const isVideo = accept.includes("video");
  const Icon = isVideo ? Video : (isPdf ? FileText : ImageIcon);

  return (
    <div className="space-y-2">
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={(e) => e.target.files?.length && upload(Array.from(e.target.files))}
        className="hidden"
        data-testid={testId ? `${testId}-input` : undefined}
      />

      {variant === "gallery" ? (
        <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
          {list.map((fid, i) => (
            <div key={fid + i} className="relative aspect-square rounded-lg overflow-hidden border border-cyan/25 bg-black/40" data-testid={testId ? `${testId}-item-${i}` : undefined}>
              {accept.includes("image") ? (
                <img src={`/api/files/${fid}`} alt="" className="w-full h-full object-cover" />
              ) : accept.includes("video") ? (
                <video src={`/api/files/${fid}`} className="w-full h-full object-cover" muted />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center gap-1 text-white/70">
                  <FileText className="h-6 w-6" />
                  <span className="mono text-[10px]">FILE</span>
                </div>
              )}
              <button type="button" onClick={() => remove(i)} className="absolute top-1 right-1 bg-black/70 hover:bg-red-500 rounded-full p-1 text-white">
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={pick}
            disabled={busy}
            data-testid={testId ? `${testId}-add` : undefined}
            className="aspect-square rounded-lg border border-dashed border-cyan/40 hover:border-cyan hover:bg-cyan/[0.05] flex flex-col items-center justify-center gap-1 text-cyan"
          >
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Icon className="h-5 w-5" /><span className="mono text-[10px]">ADD</span></>}
          </button>
        </div>
      ) : (
        <div className="flex gap-2 items-start flex-wrap">
          {list.map((fid, i) => (
            <span key={fid + i} className="inline-flex items-center gap-2 pl-3 pr-1 py-1.5 rounded-full text-xs mono border border-cyan/40 bg-cyan/[0.08] text-cyan" data-testid={testId ? `${testId}-pill-${i}` : undefined}>
              <Icon className="h-3 w-3" />
              <a href={`/api/files/${fid}`} target="_blank" rel="noreferrer" className="underline underline-offset-2">{fid.slice(0, 8)}…</a>
              <button type="button" onClick={() => remove(i)} className="p-0.5 hover:text-white opacity-70 hover:opacity-100">
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
          <button
            type="button"
            onClick={pick}
            disabled={busy}
            data-testid={testId}
            className="btn"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Upload className="h-3.5 w-3.5" /> {list.length ? "Add more" : "Upload"}</>}
          </button>
        </div>
      )}
    </div>
  );
}
